
// src/contexts/AuthContext.tsx
"use client";

import type { User } from "firebase/auth";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { auth, getFirebaseInitializationError } from "@/lib/firebase/client";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  authError: string | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: typeof window !== "undefined", // Solo carga en el cliente inicialmente
  authError: null,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  // Iniciar loading como true solo en el cliente, para SSR/dev pages sin auth, no mostrar loader.
  const [loading, setLoading] = useState(typeof window !== "undefined"); 
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[AuthContext] AuthProvider useEffect running.');
    const firebaseError = getFirebaseInitializationError();
    if (firebaseError) {
      console.error('[AuthContext] Firebase initialization error:', firebaseError);
      setAuthError(firebaseError);
      setLoading(false);
      return;
    }

    if (!auth) {
      const errMsg = "Servicio de Autenticación de Firebase no pudo ser inicializado. AuthContext no puede suscribirse a los cambios de estado de autenticación.";
      console.error(`[AuthContext] ${errMsg}`);
      setAuthError(errMsg);
      setLoading(false);
      return;
    }

    console.log('[AuthContext] Subscribing to onAuthStateChanged.');
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      console.log('[AuthContext] onAuthStateChanged triggered. firebaseUser:', firebaseUser ? firebaseUser.uid : null);
      setUser(firebaseUser);
      setLoading(false); // Termina de cargar una vez que se recibe el primer estado de auth.
    });

    return () => {
      console.log('[AuthContext] Unsubscribing from onAuthStateChanged.');
      unsubscribe();
    };
  }, []);

  if (authError && typeof window !== "undefined") { 
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-center">
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle>Error de Configuración de Firebase</AlertTitle>
          <AlertDescription>
            <p className="mb-2">No se pudo inicializar Firebase. Esto a menudo significa que falta una configuración esencial o es incorrecta.</p>
            <p className="mb-1 font-semibold">Detalles:</p>
            <pre className="mb-3 whitespace-pre-wrap rounded bg-destructive/20 p-2 text-sm text-destructive-foreground">
              {authError}
            </pre>
            <p className="text-sm text-muted-foreground">
              Por favor, asegúrate de que tus variables de entorno de Firebase (especialmente <code>NEXT_PUBLIC_FIREBASE_API_KEY</code>) estén correctamente configuradas en tu archivo <code>.env.local</code> para desarrollo local, y en la configuración de tu proveedor de hosting para producción.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <AuthContext.Provider value={{ user, loading, authError }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
